import { useEffect, type ComponentProps } from "react";
import { useStaleBuildGuard } from "../../../features/release/useStaleBuildGuard";
import { useApolloClient } from "@apollo/client/react";
import { createGuestCheckoutPort } from "../../../features/guestCheckout/adapter";
import { checkoutUnresolved, identityClaim } from "../../../features/guestCheckout/machine";
import type { HoldState } from "../../../features/booking/useHold";
import type { GuestCheckoutPort } from "../../../features/guestCheckout/port";
import { useGuestIdentityClaim } from "../../../lib/guestSession";
import { useHoldSessionEnd } from "../../../auth/sessionEndGuard";
import { GuestReviewRoute, useFlowGuestCheckout } from "./GuestReviewRoute";
import { useTermsConsent } from "../../../features/terms/useTermsConsent";
import type { TermsRead } from "../../../features/terms/operations";
import { termsLocaleFor, type TermsLocale } from "../../../features/terms/terms";
import { TermsDocumentContext } from "../../../features/terms/TermsDocumentContext";
import { useTranslation } from "../../../i18n/useTranslation";
import { FlowShellPortal } from "../../../components/PageShell";

/**
 * THE GUEST CHECKOUT'S HOME IN THE FLOW. It owns the checkout for the whole
 * flow: `Book` mounts this at the same place on every step, so leaving Review
 * for the time step (hold ended) does not unmount the checkout or lose its
 * key. It renders the guest review only when `active`; otherwise it renders
 * nothing and simply keeps state.
 */
export default function GuestFlowHost({
  port,
  hold,
  active,
  onContactRejected,
  readTerms,
  termsScope,
  onOutcomeShown,
  onUnresolved,
  ...route
}: {
  /** Injected by a test or the harness; otherwise the 1.11 adapter is built here. */
  port?: GuestCheckoutPort | null;
  hold: HoldState;
  active: boolean;
  /**
   * The booking refused a contact field (the `invalidArgs` path, or null).
   * Book sends the guest to Details with that field flagged; the proof and key
   * are kept (machine.ts, `contactRejection`).
   */
  onContactRejected?: (field: string | null) => void;
  /** Book's Terms read (the public booking API) and what a tick is for. */
  readTerms: (locale: TermsLocale) => Promise<TermsRead>;
  termsScope: string;
  /**
   * The UID a CONFIRMED or REQUESTED outcome on screen was made under (or an
   * "already booked" answer names), or null
   * when none is on screen. Reported both ways, every time it changes, so Book
   * resets when the guest leaves the outcome: Book retires that anonymous
   * identity (auth/guestRotation.ts) and, while it shows, stops claiming for
   * the hold the booking consumed. The guest keeps the booking through its
   * management link, which needs no session.
   */
  onOutcomeShown?: (uid: string | null) => void;
  /**
   * Whether this checkout is UNRESOLVED: a call in flight (requesting a
   * code, verifying it, sending the booking) or the booking's outcome
   * unknown. Reported every time it changes. Book folds it into
   * `bookingSafe`, so the sign-in screen, its link on the service step and
   * /book/create offer no identity change while it holds: a sign-in would
   * swap the UID and lose the same-UID Check. A live code, proof or limiter
   * window does NOT hold it (reviewer P2, rc.2): no booking can exist yet,
   * so switching accounts only abandons the guest path, which is the
   * person's choice to make.
   */
  onUnresolved?: (unresolved: boolean) => void;
} & Omit<ComponentProps<typeof GuestReviewRoute>, "checkout" | "hold" | "terms">) {
  const { locale } = useTranslation();
  const client = useApolloClient();
  // The React Compiler memoizes this; no manual useMemo (review A3).
  const adapter = port ?? createGuestCheckoutPort(client);
  const checkout = useFlowGuestCheckout(adapter, hold, route.details.email, active);
  const rejection = checkout.state.contactRejection;
  const { contactRejectionSeen } = checkout;
  useEffect(() => {
    if (!rejection) return;
    onContactRejected?.(rejection.field);
    contactRejectionSeen();
  }, [rejection, onContactRejected, contactRejectionSeen]);
  /*
   * THE CHECKOUT'S KEY, CODE AND PROOF ARE BOUND TO THE UID (security audit
   * M1). The identity is claimed only while something this page can still do
   * needs it, and only until the instant that ends it (F1, N1, N4): mid-call,
   * for a bounded time while an unknown outcome waits for its same-UID Check,
   * and while a code, proof or limiter window is live. Never on a finished or
   * failed screen, which an abandoned tab could otherwise hold forever
   * (machine.ts, `identityClaim`).
   */
  const { attempt, phase } = checkout.state;
  const claim = identityClaim(checkout.state);
  useGuestIdentityClaim(claim !== null, claim?.until, claim?.maxMs);
  // Book's identity-change gates (reviewer P2, rc.2). The account path's
  // equivalent is useBooking's state, which a guest never produces: this
  // checkout owns every anonymous commit. `uncertain` holds for as long as
  // it shows, as the account path's does, whether or not a Check remains.
  // Unresolved: a call in flight, an unknown outcome, or a possibly-committed
  // original still held (machine.ts `checkoutUnresolved`).
  const unresolved = checkoutUnresolved(checkout.state);
  // A newer release found while the code request, verification or booking is
  // in flight or uncertain, or its outcome is on screen, must not offer a
  // reload over it (rc.9 item 2; review P2): the same outcome phases the
  // session-end hold protects.
  useStaleBuildGuard(
    unresolved || (active && (phase.name === "done" || phase.name === "unauthorized" || phase.name === "already-booked-unreachable"))
  );
  useEffect(() => {
    onUnresolved?.(unresolved);
  }, [unresolved, onUnresolved]);
  // A session end waits while the booking is sending, unknown, or its answer
  // is on screen (auth/sessionEndGuard), as on the account path. A guest's
  // session is never ended from the transport, so this only matters if a
  // credentialed identity ever reaches this checkout.
  useHoldSessionEnd(
    phase.name === "submitting" ||
      phase.name === "uncertain" ||
      (active && (phase.name === "done" || phase.name === "unauthorized" || phase.name === "already-booked-unreachable"))
  );
  // Only once the outcome is RENDERED, which it is only while active; and
  // null again once it is not, so Book never keeps a stale outcome.
  // `already-booked-unreachable` too (security audit ruling 1): Backend says
  // a booking exists under this UID and this page can never reach it, so the
  // identity is retired as it is after a definite outcome.
  const bookedUnderUid = phase.name === "done" || phase.name === "already-booked-unreachable";
  const outcomeUid = active && bookedUnderUid ? (attempt?.uid ?? null) : null;
  // No booking consumed the live hold here, so release it under the UID that
  // owns it BEFORE that UID is retired (rotation waits for the release, as it
  // does for `resolvedPrior`), or the slot stays blocked until it expires.
  // Declared before the report below, so the release is already on the wire.
  // rc.11 1c (QE P2): the same when the booking shown is the ORIGINAL request's
  // and the live hold is another one, picked again from the uncertainty; the
  // booking consumed its own hold, not this one. Release is best effort
  // (useHold): a failed one leaves Backend's expiry to free the slot.
  const bookedHoldId = phase.name === "done" ? (checkout.state.submission?.input.holdId ?? null) : null;
  const strayHold =
    active &&
    hold.status === "held" &&
    (phase.name === "already-booked-unreachable" || (bookedHoldId !== null && hold.hold.id !== bookedHoldId));
  const { dropHold } = route;
  useEffect(() => {
    if (strayHold) void dropHold?.();
  }, [strayHold, dropHold]);
  useEffect(() => {
    onOutcomeShown?.(outcomeUid);
  }, [outcomeUid, onOutcomeShown]);
  /*
   * THE GUEST'S TERMS, owned here beside the checkout: the machine's refusal
   * object reaches the hook in the same render that stores it, so the tick
   * is gone before any frame could offer it again (reviewer P3). A new
   * object per refusal; the hook re-reads (stale, required) or shows 4.5
   * (unavailable). A READ, never a resend.
   */
  const terms = useTermsConsent({
    read: readTerms,
    locale: termsLocaleFor(locale),
    enabled: active,
    scope: termsScope,
    refusal: checkout.state.termsRefusal,
  });
  // The guest outcome renders in here, so its footer links the same document.
  // INTO Book's flow shell (c6-2): this host keeps its tree position, so all
  // of the state above survives step changes, while its screens render in
  // the shell's <main> and keep its header. No shell mounted: nothing.
  return active ? (
    <FlowShellPortal>
      <TermsDocumentContext.Provider value={terms.documentUrl}>
        <GuestReviewRoute checkout={checkout} hold={hold} terms={terms} {...route} />
      </TermsDocumentContext.Provider>
    </FlowShellPortal>
  ) : null;
}
